// apps/mtd-itsa-web/src/pages/settings/quarterly-type.tsx

import { useEffect, useState } from 'react';
import { Button } from '../../components/base/buttons/button'; // Ensure this is used or remove if unused
import { Select } from '../../components/base/select/select';
import { useHandleError } from '../../hooks/use-handle-error';
import { useToast } from '../../components/application/toast';
import { useProject } from '../../providers/project-provider';
import { getSupabaseClient } from '@kordbooks/supabase-client';
import { useHmrcApi } from '@/hooks/use-hmrc-api';

// Define the shape of the business data from your DB
interface BusinessSettings {
    businessid: string;
    tradingname: string | null;
    typeofbusiness: string;
    quarterlyperiodtype: 'standard' | 'calendar' | null;
    quarterlytaxyearofchoice: string | null;
}

type PeriodType = 'standard' | 'calendar';

const periodOptions = [
    { id: 'standard', label: 'Standard (6 Apr - 5 Jul)' },
    { id: 'calendar', label: 'Calendar (1 Apr - 30 Jun)' },
];

export default function QuarterlyTypePage() {
    const { currentProjectId } = useProject();
    const handleError = useHandleError();
    const toast = useToast();
    const api = useHmrcApi(currentProjectId || undefined);


    const [businesses, setBusinesses] = useState<BusinessSettings[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [changes, setChanges] = useState<Record<string, PeriodType>>({});

    // Calculate current tax year (e.g. "2025-26")
    // Fixed to current tax year per guidance; no state setter needed.
    const selectedTaxYear = (() => {
        const now = new Date();
        // Tax year starts April 6th
        const year = now.getMonth() > 2 || (now.getMonth() === 3 && now.getDate() >= 6)
            ? now.getFullYear()
            : now.getFullYear() - 1;
        return `${year}-${(year + 1).toString().slice(-2)}`;
    })();

    // Fetch businesses from local DB to display current settings
    useEffect(() => {
        async function fetchBusinesses() {
            if (!currentProjectId) return;
            setLoading(true);
            try {
                const supabase = getSupabaseClient();
                const { data, error } = await supabase
                    .schema('mtd_itsa')
                    .from('hmrc_businesses')
                    .select('business_id, trading_name, type_of_business, quarterly_period_type, quarterly_tax_year_of_choice')
                    .eq('project_id', currentProjectId)
                    .order('created_at', { ascending: true });

                if (error) throw error;

                setBusinesses(
                    (data || []).map((b) => ({
                        businessid: b.business_id,
                        tradingname: b.trading_name,
                        typeofbusiness: b.type_of_business,
                        quarterlyperiodtype: b.quarterly_period_type as 'standard' | 'calendar' | null,
                        quarterlytaxyearofchoice: b.quarterly_tax_year_of_choice,
                    }))
                );
            } catch (err) {
                handleError(err, 'Failed to load businesses');
            } finally {
                setLoading(false);
            }
        }

        fetchBusinesses();
    }, [currentProjectId, handleError]);

    const handlePeriodChange = (businessId: string, newType: PeriodType) => {
        setChanges((prev) => ({
            ...prev,
            [businessId]: newType,
        }));
    };

    const handleSave = async () => {
        if (Object.keys(changes).length === 0) return;

        setSaving(true);
        const apiUrl = import.meta.env.VITE_API_URL;

        let successCount = 0;
        let failCount = 0;

        try {
            await Promise.all(
                Object.entries(changes).map(async ([businessId, type]) => {
                    try {
                        const response = await api.put(
                            `${apiUrl}/api/hmrc/business-details/${businessId}/${selectedTaxYear}`,
                            { quarterlyPeriodType: type }
                        );

                        if (!response.ok) {
                            const errData = await response.json().catch(() => ({}));
                            throw new Error(errData.message || 'Update failed');
                        }
                        successCount++;
                    } catch (err) {
                        console.error(`Failed to update business ${businessId}`, err);
                        failCount++;
                    }
                })
            );

            if (failCount === 0) {
                toast.toast({
                    title: 'Settings updated',
                    description: `Successfully updated.`,
                    variant: 'success',
                });
                setChanges({});
                // Optimistically update local state
                setBusinesses((prev) =>
                    prev.map((b) =>
                        changes[b.businessid]
                            ? { ...b, quarterlyperiodtype: changes[b.businessid] }
                            : b
                    )
                );
            } else {
                toast.toast({
                    title: 'Partial Update',
                    description: `Updated ${successCount} businesses, but ${failCount} failed. Check HMRC portal or try again.`,
                    variant: 'warning',
                });
            }
        } catch (err) {
            handleError(err, 'Failed to save settings');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-6">
            {/* Header Section */}
            <div>
                <h1 className="text-2xl font-semibold text-gray-900">Quarterly Period Type</h1>
                <p className="mt-1 text-sm text-gray-500">
                    Choose between standard or calendar quarters for your businesses.
                </p>
            </div>

            {/* Tax Year Display (Updated) */}
            <div className="mb-6">
                <span className="block text-sm font-medium text-gray-700">Tax Year</span>
                <div className="mt-1 block w-full py-2 text-base text-gray-900">
                    {selectedTaxYear}
                </div>
                <p className="text-xs text-gray-500">
                    Updates are restricted to the current tax year.
                </p>
            </div>

            {/* Main Content Area */}
            {loading ? (
                <div className="text-gray-500">Loading businesses...</div>
            ) : businesses.length === 0 ? (
                <div className="text-gray-500">
                    No businesses found for this project. Please sync your obligations first.
                </div>
            ) : (
                // ...existing code...
                <div className="w-full overflow-x-auto scrollbar-visible pb-2">
                    <table className="min-w-full divide-y divide-gray-300">
                        <thead className="bg-gray-50">
                            <tr>
                                <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6 min-w-[200px]">
                                    Business Details
                                </th>
                                <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 min-w-[180px]">
                                    New Setting
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white">
                            {businesses.map((business) => {
                                const pendingValue = changes[business.businessid];
                                const currentValue = business.quarterlyperiodtype;

                                return (
                                    <tr key={business.businessid}>
                                        {/* Business Info */}
                                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-6 align-top">
                                            <div className="font-medium text-gray-900">
                                                {business.tradingname || 'Unknown Trading Name'}
                                            </div>
                                            <div className="flex flex-col sm:flex-row sm:space-x-2 text-gray-500 text-xs sm:text-sm mt-1">
                                                <span>{business.typeofbusiness}</span>
                                                <span className="sm:inline block">•</span>
                                                <span className="break-all">{business.businessid}</span>
                                            </div>
                                        </td>

                                        {/* Dropdown Action */}
                                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 align-top">
                                            <div className="w-64 max-w-full">
                                                <Select
                                                    items={periodOptions}
                                                    value={pendingValue || currentValue}
                                                    onChange={(key) => handlePeriodChange(business.businessid, key as PeriodType)}
                                                    aria-label={`Select period type for ${business.tradingname}`}
                                                >
                                                    {(item) => (
                                                        <Select.Item id={item.id} textValue={item.label}>
                                                            {item.label}
                                                        </Select.Item>
                                                    )}
                                                </Select>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Footer Actions */}
            <div className="flex items-center justify-end gap-4 border-t border-gray-200 pt-4">
                {Object.keys(changes).length > 0 && (
                    <span className="text-sm text-gray-500">
                        {Object.keys(changes).length} change(s) pending
                    </span>
                )}
                <Button onClick={handleSave} disabled={saving || Object.keys(changes).length === 0}>
                    {saving ? 'Saving...' : 'Save Changes'}
                </Button>
            </div>
        </div>
    );
}
